import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_UI_MENU_POSITION,
  isUIMenuPosition,
  UI_MENU_POSITION_STORAGE_KEY,
  UIMenuPosition,
} from '@/utils/persistentPreferences'
import Cookies from 'js-cookie'

type UIVersionContextType = {
  isNewUI: boolean
  menuPosition: UIMenuPosition
  enableNewUI: () => void
  disableNewUI: () => void
  setMenuPosition: (position: UIMenuPosition) => void
}

const UIVersionContext = createContext<UIVersionContextType | undefined>(undefined)

const UI_VERSION_COOKIE = 'tickmill_ui_version'
const UI_VERSION_PARAM = 'spaVersion'
const NEW_UI_VALUE = '1.98'

const getUrlVersion = () => {
  if (typeof window === 'undefined') {
    return null
  }

  return new URLSearchParams(window.location.search).get(UI_VERSION_PARAM)
}

const getStoredMenuPosition = (): UIMenuPosition => {
  if (typeof window === 'undefined') {
    return DEFAULT_UI_MENU_POSITION
  }

  const storedPosition = window.localStorage.getItem(UI_MENU_POSITION_STORAGE_KEY)
  return isUIMenuPosition(storedPosition) ? storedPosition : DEFAULT_UI_MENU_POSITION
}

export function UIVersionProvider({ children }: { children: React.ReactNode }) {
  const [isNewUI, setIsNewUI] = useState<boolean>(() => {
    // Priority: 1. URL param, 2. Cookie, 3. Default (false - old UI)
    const urlVersion = getUrlVersion()
    if (urlVersion === NEW_UI_VALUE) return true

    const cookieVersion = Cookies.get(UI_VERSION_COOKIE)
    if (cookieVersion === NEW_UI_VALUE) return true

    return false // Default to old UI
  })
  const [menuPositionState, setMenuPositionState] = useState<UIMenuPosition>(getStoredMenuPosition)

  useEffect(() => {
    const urlVersion = getUrlVersion()

    if (urlVersion === NEW_UI_VALUE) {
      setIsNewUI(true)
      // Save to cookie (30 days expiry)
      Cookies.set(UI_VERSION_COOKIE, NEW_UI_VALUE, { expires: 30 })
    }
  }, [])

  useEffect(() => {
    const version = isNewUI ? 'new' : 'legacy'

    document.documentElement.dataset.uiVersion = version
    document.documentElement.dataset.uiMenuPosition = menuPositionState
    document.body.dataset.uiVersion = version
    document.body.dataset.uiMenuPosition = menuPositionState

    return () => {
      delete document.documentElement.dataset.uiVersion
      delete document.documentElement.dataset.uiMenuPosition
      delete document.body.dataset.uiVersion
      delete document.body.dataset.uiMenuPosition
    }
  }, [isNewUI, menuPositionState])

  const enableNewUI = () => {
    setIsNewUI(true)
    Cookies.set(UI_VERSION_COOKIE, NEW_UI_VALUE, { expires: 30 })
  }

  const disableNewUI = () => {
    setIsNewUI(false)
    Cookies.remove(UI_VERSION_COOKIE)
  }

  const setMenuPosition = (position: UIMenuPosition) => {
    setMenuPositionState(position)

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(UI_MENU_POSITION_STORAGE_KEY, position)
    }
  }

  const contextValue = useMemo(
    () => ({
      isNewUI,
      menuPosition: menuPositionState,
      enableNewUI,
      disableNewUI,
      setMenuPosition,
    }),
    [isNewUI, menuPositionState]
  )

  return <UIVersionContext.Provider value={contextValue}>{children}</UIVersionContext.Provider>
}

export function useUIVersion() {
  const context = useContext(UIVersionContext)
  if (context === undefined) {
    throw new Error('useUIVersion must be used within UIVersionProvider')
  }
  return context
}
